import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Card, Alert, Skeleton } from '@dreamtree-org/twreact-ui';
import { UserPlus } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { setAuth } from '@rbac/permissions';

const errorOf = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message || 'Something went wrong';

/**
 * Join from a staff invite (D14): /admin/join?token=… — set your name and a
 * password; the account gets the invited role and you are signed in.
 */
export default function Join({ store }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { fetchApi } = useAppApi();
  const [invite, setInvite] = useState(null);
  const [gone, setGone] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const storeName = (store && store.name) || 'MerchantPro';

  useEffect(() => {
    fetchApi('staffInvitePreview', { token }, false).then((r) => setInvite(r.data)).catch((ex) => setGone(errorOf(ex)));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await fetchApi('staffInviteJoin', { token, name, password }, false);
      const res = await fetchApi('login', { email: r.data.email, password });
      setAuth((res && res.data) || res);
      nav('/', { replace: true });
    } catch (ex) {
      setErr(errorOf(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-4 pt-safe pb-safe bg-[var(--app-bg)]">
      <Card className="w-full max-w-sm p-6 space-y-5">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-950">
            <UserPlus size={24} aria-hidden />
          </div>
          <h1 className="text-xl font-bold text-primary-600">{storeName}</h1>
          <p className="text-sm font-medium">Join the team</p>
        </div>
        {gone ? (
          <Alert variant="error" title="This invite can't be used">{gone}</Alert>
        ) : !invite ? (
          <Skeleton count={3} />
        ) : (
          <>
            <p className="text-sm text-center opacity-80">You were invited as <strong>{invite.role}</strong> ({invite.email}).</p>
            {err ? <Alert variant="error">{err}</Alert> : null}
            <form onSubmit={submit} className="space-y-4">
              <Input label="Your name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />
              <Input label="Choose a password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} helperText="At least 10 characters, with letters and numbers." required />
              <Button type="submit" fullWidth loading={busy} disabled={!name.trim() || password.length < 10}>Create my account</Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
